import React from "react";
import { IconButton, ListItem, Theme, WithStyles, createStyles, withStyles } from "@material-ui/core";
import { CameraAlt, Edit } from "@material-ui/icons";
import { User } from "../../types/user";
import { Player } from "../../types/player";
import { getHandicapDisplay } from "../../utils";
import config from "../../config";
import { MOBILE_BREAKPOINT } from "../../consts/dialog";
import { GAME_MENU_HEADER_BORDER, LIVE_GAME_MENU_COLORS, RANGE_GAME_COLORS } from "./rangeGameStyles";

const formatHcp = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (typeof n !== "number" || Number.isNaN(n)) return "-";
  return getHandicapDisplay(n);
};

const playingHcpForGame = (player: Player | undefined, competitionId?: string) => {
  if (!player) return undefined;
  const key = competitionId != null ? String(competitionId) : "";
  const fromMap = key && player.playingHcps ? player.playingHcps[key] : undefined;
  if (fromMap != null && fromMap !== "") return fromMap;
  return player.hcp ?? player.playingHcp;
};

export type GameMenuPlayerHeaderProps = {
  user: User;
  player?: Player;
  imPlaying: boolean;
  isRange: boolean;
  competitionId?: string;
  canEditHi: boolean;
  onEditHi: () => void;
  onChangePhoto: () => void;
};

type Props = GameMenuPlayerHeaderProps & WithStyles<typeof styles>;

const GameMenuPlayerHeader = ({
  user,
  player,
  imPlaying,
  isRange,
  competitionId,
  canEditHi,
  onEditHi,
  onChangePhoto,
  classes,
}: Props) => {
  const displayName = [user.name, user.lastName].filter(Boolean).join(" ").trim() || user.username;
  const avatarPath = user.avatar || player?.avatar;
  const avatarSrc = avatarPath ? config.API_URL + avatarPath : undefined;
  const initials = `${(user.name?.[0] || "").toUpperCase()}${(user.lastName?.[0] || "").toUpperCase()}`;
  const whsIndex = formatHcp(imPlaying && player ? player.hci : user.hcp);
  const courseHcp = imPlaying && player ? formatHcp(player.courseHandicap) : "-";
  const playingHcp = imPlaying && player ? formatHcp(playingHcpForGame(player, competitionId)) : "-";

  return (
    <ListItem
      className={isRange ? classes.rootRange : classes.root}
      disableGutters
      classes={{ container: classes.listItemContainer }}
    >
      <div className={classes.block}>
        <div className={classes.identity}>
          <div className={classes.avatarWrap}>
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className={classes.avatar} />
            ) : (
              <div className={classes.avatar} aria-hidden="true">
                {initials || "?"}
              </div>
            )}
            <IconButton
              size="small"
              className={classes.cameraButton}
              onClick={onChangePhoto}
              aria-label="Change profile photo"
            >
              <CameraAlt className={classes.cameraIcon} fontSize="inherit" />
            </IconButton>
          </div>
          <div className={classes.meta}>
            <span className={classes.playerName}>{displayName}</span>
            <span className={classes.username}>@{user.username}</span>
          </div>
        </div>

        <div className={classes.statsRow}>
          {canEditHi ? (
            <button
              type="button"
              className={`${classes.statBox} ${classes.statBoxButton}`}
              onClick={onEditHi}
              aria-label="Edit WHS Index"
            >
              <span className={classes.statLabel}>
                <span>WHS</span>
                <span>INDEX</span>
              </span>
              <span className={classes.statValueRow}>
                <Edit className={classes.editIcon} fontSize="inherit" />
                <span className={classes.statValue}>{whsIndex}</span>
              </span>
            </button>
          ) : (
            <div className={classes.statBox} aria-label="WHS Index">
              <span className={classes.statLabel}>
                <span>WHS</span>
                <span>INDEX</span>
              </span>
              <span className={classes.statValue}>{whsIndex}</span>
            </div>
          )}
          <div className={classes.statBox}>
            <span className={classes.statLabel}>
              <span>COURSE</span>
              <span>HCP</span>
            </span>
            <span className={classes.statValue}>{courseHcp}</span>
          </div>
          <div className={classes.statBox}>
            <span className={classes.statLabel}>
              <span>PLAYING</span>
              <span>HCP</span>
            </span>
            <span className={`${classes.statValue} ${classes.phValue}`}>{playingHcp}</span>
          </div>
        </div>
      </div>
    </ListItem>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      backgroundColor: LIVE_GAME_MENU_COLORS.backgroundColor,
      backgroundImage: LIVE_GAME_MENU_COLORS.backgroundImage,
      borderBottom: GAME_MENU_HEADER_BORDER,
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
      },
    },
    rootRange: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      backgroundColor: RANGE_GAME_COLORS.bg,
      borderBottom: GAME_MENU_HEADER_BORDER,
      [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
      },
    },
    listItemContainer: {
      width: "100%",
    },
    block: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      width: "100%",
      minWidth: 0,
      gap: theme.spacing(1.25),
    },
    identity: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      minWidth: 0,
      flex: "1 1 auto",
    },
    avatarWrap: {
      position: "relative",
      flexShrink: 0,
      width: 48,
      height: 48,
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      backgroundColor: "#673ab7",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.125rem",
      fontWeight: 700,
      objectFit: "cover",
      flexShrink: 0,
      letterSpacing: 0.5,
    },
    cameraButton: {
      position: "absolute",
      right: -4,
      bottom: -4,
      width: 22,
      height: 22,
      padding: 0,
      backgroundColor: "#fff",
      border: "1px solid rgba(15, 23, 42, 0.16)",
      color: "rgba(15, 23, 42, 0.78)",
      "&:hover": {
        backgroundColor: "#fff",
      },
    },
    cameraIcon: {
      fontSize: 14,
    },
    meta: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      gap: 2,
    },
    playerName: {
      fontWeight: 700,
      fontSize: "1.0625rem",
      lineHeight: 1.25,
      color: theme.palette.text.primary,
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflowWrap: "anywhere",
    },
    username: {
      fontSize: "0.875rem",
      lineHeight: 1.3,
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    statsRow: {
      display: "flex",
      alignItems: "stretch",
      gap: 6,
      flex: "0 0 auto",
    },
    statBox: {
      display: "flex",
      flex: "0 0 56px",
      width: 56,
      minHeight: 68,
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      border: "1px solid rgba(15, 23, 42, 0.12)",
      borderRadius: 8,
      padding: "8px 4px",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      font: "inherit",
      color: "inherit",
      textAlign: "center",
      boxSizing: "border-box",
    },
    statBoxButton: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#ffffff",
        borderColor: "rgba(15, 23, 42, 0.2)",
      },
      "&:focus": {
        outline: "2px solid rgba(25, 118, 210, 0.28)",
        outlineOffset: 1,
      },
    },
    statLabel: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      fontSize: "0.625rem",
      textTransform: "uppercase",
      letterSpacing: 0.2,
      lineHeight: 1.15,
      marginBottom: 4,
      "& > span:first-child": {
        fontWeight: 700,
        color: "rgba(0, 0, 0, 0.78)",
      },
      "& > span:last-child": {
        fontWeight: 600,
        color: "rgba(0, 0, 0, 0.68)",
      },
    },
    statValueRow: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
    },
    editIcon: {
      fontSize: 14,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    statValue: {
      fontSize: "1rem",
      fontWeight: 700,
      color: "#333",
      lineHeight: 1.15,
      fontVariantNumeric: "tabular-nums",
    },
    phValue: {
      fontWeight: 800,
    },
  });

export default withStyles(styles)(GameMenuPlayerHeader);
